import { computed, observable } from "mobx";
import {
  ExtendedModel,
  type Frozen,
  frozen,
  modelAction,
  prop,
  type SnapshotOutOf,
} from "mobx-keystone";
import { authClient } from "../../../auth/client";
import {
  loadSession,
  verifyMagicLinkCode,
  verifyMagicLinkToken,
} from "../../../auth/magic-link";
import { getRouterConfig } from "../../../config";
import { GlobalTModel } from "../../lib/models/global-t-model";
import { nsModel } from "../../lib/namespace";
import type RootStore from "../../root";

export type Session = ReturnType<typeof authClient.useSession.get>["data"];
export type SessionError = {
  message: string;
  name: string;
};

const authNs = nsModel("auth");

@authNs.store
export default class AuthStore extends ExtendedModel(GlobalTModel, {
  token: prop<string | undefined>().withSetter(),
  email: prop<string | undefined>().withSetter(),
  session: prop<Frozen<Session>>().withSetter(),
  authError: prop<Frozen<Record<string, any> | null>>(() =>
    frozen(null),
  ).withSetter(),
  sessionError: prop<Frozen<SessionError | null>>(() =>
    frozen(null),
  ).withSetter(),
}) {
  @observable
  subscribed = false;

  protected omittedKeys: (keyof SnapshotOutOf<this>)[] = [
    "token",
    "session",
    "sessionError",
  ];

  @computed
  get isAuthed() {
    return !!this.token;
  }

  @computed
  get currentSession() {
    return this.session.data;
  }

  get requireAuth(): {
    token: string;
    email: string;
  } {
    if (!this.token) {
      throw new Error("Auth is required but user is not authenticated");
    }
    if (!this.email) {
      throw new Error("Auth is required but email is not defined");
    }
    return {
      token: this.token,
      email: this.email,
    };
  }

  get ready() {
    return this.subscribed;
  }

  async signUp(options: Parameters<typeof authClient.signUp.email>[0]) {
    const authResult = await authClient.signUp.email(options);

    if (authResult.error) {
      this.setAuthError(frozen(authResult.error));
    }

    return { authResult };
  }

  async signIn(options: Parameters<typeof authClient.signIn.email>[0]) {
    const result = await authClient.signIn.email(options);

    if (result.error) {
      this.setAuthError(frozen(result.error));
    }

    return result;
  }

  async signInWithMagicLink(
    options: Parameters<typeof authClient.signIn.magicLink>[0],
  ) {
    const result = await authClient.signIn.magicLink(options);

    if (result.error) {
      this.setAuthError(frozen(result.error));
    }

    return result;
  }

  /**
   * Spends a magic link token and loads the session it creates into this store.
   *
   * @returns `true` when the user is now signed in
   */
  async verifyMagicLink(token: string): Promise<boolean> {
    const isVerified = await verifyMagicLinkToken(
      (options) => authClient.magicLink.verify(options),
      token,
    );
    if (!isVerified) return false;

    await this.refreshSession();
    return true;
  }

  /**
   * Spends the one-time code emailed alongside a magic link and loads the session it creates into
   * this store.
   *
   * @returns `true` when the user is now signed in
   */
  async verifyMagicLinkCode(email: string, code: string): Promise<boolean> {
    const isVerified = await verifyMagicLinkCode(
      (options) => authClient.signIn.emailOtp(options),
      email,
      code,
    );
    if (!isVerified) return false;

    await this.refreshSession();
    return true;
  }

  /**
   * Loads the current session from the server into this store. For a session that changed where
   * the auth client could not see it, such as a magic link verified in another tab.
   *
   * @returns `true` when a session exists
   */
  async refreshSession(): Promise<boolean> {
    const session = await loadSession(() => authClient.getSession());
    if (!session) return false;

    // the same plain-JSON copy the session subscription below stores
    this.handleSession(JSON.parse(JSON.stringify(session)));
    // the auth client keeps its own copy of the session. Reloading it keeps the two in step, which
    // the subscription below needs in order to notice a later sign-out
    authClient.$store.notify("$sessionSignal");

    return true;
  }

  async signInWithPasskey(
    options?: Parameters<typeof authClient.signIn.passkey>[0],
  ) {
    const result = await authClient.signIn.passkey(options ?? {});

    if (result.error) {
      this.setAuthError(frozen(result.error));
    }

    return result;
  }

  async registerPasskey(
    options: Parameters<typeof authClient.passkey.addPasskey>[0],
  ): ReturnType<typeof authClient.passkey.addPasskey> {
    if (!this.isAuthed) {
      throw new Error("User must be signed in to register a passkey");
    }
    const result = await authClient.passkey.addPasskey(options);
    if (result.error) {
      this.setAuthError(frozen(result.error));
    }
    return result;
  }

  async deletePasskey(
    options: Parameters<typeof authClient.passkey.deletePasskey>[0],
  ) {
    if (!this.isAuthed) {
      throw new Error("User must be signed in to delete a passkey");
    }
    const result = await authClient.passkey.deletePasskey(options ?? {});
    if (result.error) {
      this.setAuthError(frozen(result.error));
    }
    return result;
  }

  async sendVerificationEmail(
    options: Parameters<typeof authClient.sendVerificationEmail>[0],
  ) {
    if (!this.isAuthed) {
      throw new Error("User must be signed in to send a verification email");
    }
    const result = await authClient.sendVerificationEmail(options);

    if (result.error) {
      this.setAuthError(frozen(result.error));
    }

    return result;
  }

  async requestPasswordReset(
    options: Parameters<typeof authClient.requestPasswordReset>[0],
  ) {
    const result = await authClient.requestPasswordReset({
      ...options,
      redirectTo: "/reset-password",
    });

    if (result.error) {
      this.setAuthError(frozen(result.error));
    }

    return result;
  }

  async resetPassword(options: Parameters<typeof authClient.resetPassword>[0]) {
    const result = await authClient.resetPassword(options);

    if (result.error) {
      this.setAuthError(frozen(result.error));
    }

    return result;
  }

  async changePassword(
    options: Parameters<typeof authClient.changePassword>[0],
  ) {
    if (!this.isAuthed) {
      throw new Error("User must be signed in to change password");
    }

    const result = await authClient.changePassword(options);

    if (result.error) {
      this.setAuthError(frozen(result.error));
    }

    return result;
  }

  async signOut(options: Parameters<typeof authClient.signOut>[0]) {
    const result = await authClient.signOut(options);

    if (result.error) {
      this.setAuthError(frozen(result.error));
    }

    return result;
  }

  @modelAction
  handleSession(session: Session) {
    if (session && typeof session === "object") {
      this.setSession(frozen(session));
      this.setEmail(session.user.email);
      this.setToken(session.session.token);
    }
  }

  protected onAttachedToRootStore(rootStore: RootStore) {
    const router = getRouterConfig().routerAdapter;

    const _currentSession = authClient.useSession.get();
    if (this.session?.data) {
      // this only happens if RootCreationData is hydrated with a session, in which case we can assume the session is valid
    } else {
      //
    }

    const unsubscribe = authClient.useSession.subscribe(
      async ({ data, error }, oldValue) => {
        if (error) {
          this.setSessionError(
            frozen({ message: error.message, name: error.name }),
          );
        }

        /**
         * If better auth receives a 200 response and the response isnt json it'll just return the response as text. The type here is a lie
         */
        if (data && typeof data === "object" && !error) {
          // set user session data
          this.setSession(frozen(JSON.parse(JSON.stringify(data))));
          this.setEmail(data.user.email);
          this.setToken(data.session.token);

          try {
            console.debug(
              "Session updated, fetching user data and waiting for x store to be ready...",
            );
            // const apiUser = await api.user.get({});

            // console.debug("User data fetched after session update", apiUser);
            // rootStore.createTilliXStore({
            //   user: {
            //     ...apiUser,
            //     primaryAddress: apiUser.primaryAddress
            //       ? {
            //           ...apiUser.primaryAddress,
            //           street2: apiUser.primaryAddress?.street2 ?? null,
            //           countryCodeId:
            //             apiUser.primaryAddress?.countryCodeId ?? null,
            //         }
            //       : null,
            //     lastSync: new Date().toISOString(),
            //     name: apiUser.name ?? null,
            //     phoneNumber: apiUser.phoneNumber ?? null,
            //     // usersTenantRefIds: [],
            //     // TODO: these need to be returned from the backend
            //     createdAt: new Date().toISOString(),
            //     updatedAt: new Date().toISOString(),
            //   },
            // });

            // await when(() => rootStore.x?.ready ?? false);

            // if (!router.isAuthenticatedPath()) {
            //   console.log(
            //     "🚀 🌷 AuthStore onAttachedToRootStore hit router.replace(/) 🌷",
            //   );
            //   void router.replace("/");
            //   console.log(
            //     "🚀 🌷 AuthStore onAttachedToRootStore hit router.replace(/) done 🌷",
            //   );
            // }
            console.debug("User data and x store ready after session update");
          } catch (e) {
            console.error("Error fetching user data after session update", e);
          }
        } else if (
          (error || (!data && oldValue?.data)) &&
          router.isAuthenticatedPath()
        ) {
          void router.redirectToAuth(router.getPath());
        }
      },
    );

    this.subscribed = true;

    return unsubscribe;
  }
}
